import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  SwapHoriz as SwapIcon,
  RestartAlt as ResetIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { getBracketNames } from "../utils/bracketNames";
import { useOrganization } from "../contexts/OrganizationContext";
import { AdvancementRule } from "../types";

// Custom seeding data structure
export interface CustomSeedingData {
  mastersCup?: string[]; // Flat ordered array: [team1Match1, team2Match1, team1Match2, team2Match2, ...]
  tourCup?: string[];
  singleBracket?: string[];
}

interface KnockoutSeedingPreviewProps {
  open: boolean;
  onClose: () => void;
  numberOfGroups: number;
  teamsPerGroup: number;
  knockoutFormatType: "single-bracket" | "double-bracket";
  mastersCupAdvanceCount?: number; // For double-bracket
  tourCupAdvanceCount?: number; // For double-bracket
  knockoutAdvanceCount?: number; // For single-bracket
  mastersCupAdvancementRules?: AdvancementRule[]; // Overrides mastersCupAdvanceCount when set
  tourCupAdvancementRules?: AdvancementRule[]; // Overrides tourCupAdvanceCount when set
  onSeedingChange?: (seeding: CustomSeedingData) => void;
  initialCustomSeeding?: CustomSeedingData;
}

// Mirrors the backend's/scheduleUtils' claiming logic, but with descriptor labels instead of
// team IDs since the actual team can't be known until groups finish playing.
const buildAdvancementDescriptorList = (
  rules: AdvancementRule[],
  numberOfGroups: number,
  teamsPerGroup: number,
  groupNames: string[],
  claimedGroupPositions: Set<string>,
  wildcardBudgetByPosition: Map<number, number>,
): string[] => {
  const getPositionLabel = (position: number) =>
    position === 0 ? "1st" : position === 1 ? "2nd" : position === 2 ? "3rd" : `${position + 1}th`;

  const descriptors: string[] = [];

  for (const rule of rules) {
    if (rule.type === "group-position") {
      for (let g = 0; g < numberOfGroups; g++) {
        const key = `${rule.position}-${g}`;
        if (claimedGroupPositions.has(key)) continue;
        claimedGroupPositions.add(key);
        descriptors.push(`${getPositionLabel(rule.position)} ${groupNames[g]}`);
      }
    } else if (rule.type === "best-of-position-across-groups") {
      // Don't claim specific groups here — which group(s) end up NOT being the "best"
      // picks isn't knowable until standings are final, so only the budget is reserved.
      // The 'remaining' branch below turns that budget into generic (non-group) leftovers.
      wildcardBudgetByPosition.set(
        rule.position,
        (wildcardBudgetByPosition.get(rule.position) || 0) + rule.count,
      );
      for (let i = 0; i < rule.count; i++) {
        descriptors.push(`Best ${getPositionLabel(rule.position)} Place #${i + 1}`);
      }
    } else {
      // 'remaining': every group-position slot not yet claimed, minus any wildcard
      // budget already consumed elsewhere at that position. When a budget applies, the
      // leftover slot(s) can't be tied to a specific group (any of the unclaimed groups
      // could end up being the one not picked by the wildcard rule), so they get a
      // generic label instead of a group-specific one.
      for (let position = 0; position < teamsPerGroup; position++) {
        const availableGroups: number[] = [];
        for (let g = 0; g < numberOfGroups; g++) {
          const key = `${position}-${g}`;
          if (!claimedGroupPositions.has(key)) availableGroups.push(g);
        }
        const budget = wildcardBudgetByPosition.get(position) || 0;
        const keep = Math.max(0, availableGroups.length - budget);
        if (budget > 0) {
          for (let i = 0; i < keep; i++) {
            descriptors.push(`${getPositionLabel(position)} Place #${i + 1}`);
          }
          availableGroups.forEach(g => claimedGroupPositions.add(`${position}-${g}`));
        } else {
          availableGroups.slice(0, keep).forEach(g => {
            claimedGroupPositions.add(`${position}-${g}`);
            descriptors.push(`${getPositionLabel(position)} ${groupNames[g]}`);
          });
        }
      }
    }
  }

  return descriptors;
};

// --- Draggable Chip wrapper ---
interface DraggableChipProps {
  id: string;
  label: string;
  color: "primary" | "secondary" | "info";
  variant?: "filled" | "outlined";
  isOver?: boolean;
}

const DraggableChip: React.FC<DraggableChipProps> = ({ id, label, color, variant = "filled", isOver }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  const { setNodeRef: setDropRef, isOver: isDropOver } = useDroppable({ id });

  const highlighted = isOver || isDropOver;

  return (
    <Box
      ref={(node: HTMLDivElement | null) => {
        setNodeRef(node);
        setDropRef(node);
      }}
      {...listeners}
      {...attributes}
      sx={{
        cursor: "grab",
        opacity: isDragging ? 0.3 : 1,
        transform: highlighted ? "scale(1.08)" : "none",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
    >
      <Chip
        icon={<DragIcon sx={{ fontSize: 14 }} />}
        label={label}
        size="small"
        sx={{
          fontWeight: "bold",
          mb: variant === "filled" ? 0.5 : 0,
          boxShadow: highlighted ? `0 0 0 2px` : "none",
          borderStyle: highlighted ? "dashed" : undefined,
        }}
        color={color}
        variant={variant}
      />
    </Box>
  );
};

const KnockoutSeedingPreview: React.FC<KnockoutSeedingPreviewProps> = ({
  open,
  onClose,
  numberOfGroups,
  teamsPerGroup,
  knockoutFormatType,
  mastersCupAdvanceCount = 2,
  tourCupAdvanceCount = 2,
  knockoutAdvanceCount = 2,
  mastersCupAdvancementRules,
  tourCupAdvancementRules,
  onSeedingChange,
  initialCustomSeeding,
}) => {
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);
  // Mutable pairings state for each bracket type
  const [mastersCupPairingsState, setMastersCupPairingsState] = useState<Array<{ team1: string; team2: string }>>([]);
  const [tourCupPairingsState, setTourCupPairingsState] = useState<Array<{ team1: string; team2: string }>>([]);
  const [singleBracketPairingsState, setSingleBracketPairingsState] = useState<Array<{ team1: string; team2: string }>>([]);
  const [isCustomized, setIsCustomized] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  // Generate group names
  const groupNames = Array.from({ length: numberOfGroups }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  // Generate seeding for double-bracket (Masters Cup)
  const generateMastersCupSeeding = (): Array<{ team1: string; team2: string }> => {
    const teamDescriptions: string[] = [];
    const teamsByPosition: { [position: number]: string[] } = {};

    // Validate input
    if (!mastersCupAdvanceCount || mastersCupAdvanceCount < 1) {
      return [];
    }

    // Collect teams by position
    for (let position = 0; position < mastersCupAdvanceCount; position++) {
      teamsByPosition[position] = [];
      for (let groupIndex = 0; groupIndex < numberOfGroups; groupIndex++) {
        const positionLabel = position === 0 ? "1st" : position === 1 ? "2nd" : `${position + 1}th`;
        teamsByPosition[position].push(`${positionLabel} ${groupNames[groupIndex]}`);
      }
    }

    // Apply cross-seeding
    if (mastersCupAdvanceCount === 1) {
      // Only one rank advances — cross-seed within the group:
      // pair top half (seeds 1..n/2) against reversed bottom half (seeds n/2+1..n)
      const allTeams = teamsByPosition[0] || [];
      const half = Math.floor(allTeams.length / 2);
      const top = allTeams.slice(0, half);
      const bottom = [...allTeams.slice(half)].reverse();
      for (let i = 0; i < top.length; i++) {
        teamDescriptions.push(top[i]);
        if (bottom[i]) teamDescriptions.push(bottom[i]);
      }
      // Odd team out gets a bye (just add it at the end)
      if (allTeams.length % 2 !== 0) {
        teamDescriptions.push(allTeams[allTeams.length - 1]);
      }
    } else if (mastersCupAdvanceCount === 2) {
      const firstPlace = teamsByPosition[0];
      const secondPlace = teamsByPosition[1] ? [...teamsByPosition[1]].reverse() : [];

      for (let i = 0; i < Math.max(firstPlace.length, secondPlace.length); i++) {
        if (i < firstPlace.length) teamDescriptions.push(firstPlace[i]);
        if (i < secondPlace.length) teamDescriptions.push(secondPlace[i]);
      }
    } else {
      const higherRanked = teamsByPosition[0] || [];
      const lowerRankedPos = teamsByPosition[mastersCupAdvanceCount - 1];
      const lowerRanked = lowerRankedPos ? [...lowerRankedPos].reverse() : [];

      for (let i = 0; i < Math.max(higherRanked.length, lowerRanked.length); i++) {
        if (i < higherRanked.length) teamDescriptions.push(higherRanked[i]);
        if (i < lowerRanked.length) teamDescriptions.push(lowerRanked[i]);
      }
    }

    // Create pairings for first round
    const pairings: Array<{ team1: string; team2: string }> = [];
    for (let i = 0; i < teamDescriptions.length; i += 2) {
      if (i + 1 < teamDescriptions.length) {
        pairings.push({
          team1: teamDescriptions[i],
          team2: teamDescriptions[i + 1],
        });
      } else {
        pairings.push({ team1: teamDescriptions[i], team2: "BYE" });
      }
    }

    return pairings;
  };

  // Generate seeding for Tour Cup
  const generateTourCupSeeding = (): Array<{ team1: string; team2: string }> => {
    const teamDescriptions: string[] = [];
    
    // Validate input
    if (!tourCupAdvanceCount || tourCupAdvanceCount < 1 || !mastersCupAdvanceCount) {
      return [];
    }
    
    const higherRankedPosition = mastersCupAdvanceCount;
    const lowerRankedPosition = mastersCupAdvanceCount + tourCupAdvanceCount - 1;

    const higherPosLabel =
      higherRankedPosition === 2 ? "3rd" : higherRankedPosition === 3 ? "4th" : `${higherRankedPosition + 1}th`;
    const lowerPosLabel =
      lowerRankedPosition === 2 ? "3rd" : lowerRankedPosition === 3 ? "4th" : `${lowerRankedPosition + 1}th`;

    const higherRanked: string[] = [];
    for (let groupIndex = 0; groupIndex < numberOfGroups; groupIndex++) {
      higherRanked.push(`${higherPosLabel} ${groupNames[groupIndex]}`);
    }

    const lowerRanked: string[] = [];
    for (let groupIndex = numberOfGroups - 1; groupIndex >= 0; groupIndex--) {
      lowerRanked.push(`${lowerPosLabel} ${groupNames[groupIndex]}`);
    }

    for (let i = 0; i < Math.max(higherRanked.length, lowerRanked.length); i++) {
      if (i < higherRanked.length) teamDescriptions.push(higherRanked[i]);
      if (i < lowerRanked.length) teamDescriptions.push(lowerRanked[i]);
    }

    const pairings: Array<{ team1: string; team2: string }> = [];
    for (let i = 0; i < teamDescriptions.length; i += 2) {
      if (i + 1 < teamDescriptions.length) {
        pairings.push({
          team1: teamDescriptions[i],
          team2: teamDescriptions[i + 1],
        });
      } else {
        pairings.push({ team1: teamDescriptions[i], team2: "BYE" });
      }
    }

    return pairings;
  };

  // Generate seeding for single bracket
  const generateSingleBracketSeeding = (): Array<{ team1: string; team2: string }> => {
    const teamDescriptions: string[] = [];
    const teamsByPosition: { [position: number]: string[] } = {};

    // Validate input
    if (!knockoutAdvanceCount || knockoutAdvanceCount < 1) {
      return [];
    }

    for (let position = 0; position < knockoutAdvanceCount; position++) {
      teamsByPosition[position] = [];
      for (let groupIndex = 0; groupIndex < numberOfGroups; groupIndex++) {
        const positionLabel = position === 0 ? "1st" : position === 1 ? "2nd" : `${position + 1}th`;
        teamsByPosition[position].push(`${positionLabel} ${groupNames[groupIndex]}`);
      }
    }

    // Apply cross-seeding
    if (knockoutAdvanceCount === 1) {
      // Only one rank advances — cross-seed within the group:
      // pair top half against reversed bottom half
      const allTeams = teamsByPosition[0] || [];
      const half = Math.floor(allTeams.length / 2);
      const top = allTeams.slice(0, half);
      const bottom = [...allTeams.slice(half)].reverse();
      for (let i = 0; i < top.length; i++) {
        teamDescriptions.push(top[i]);
        if (bottom[i]) teamDescriptions.push(bottom[i]);
      }
      if (allTeams.length % 2 !== 0) {
        teamDescriptions.push(allTeams[allTeams.length - 1]);
      }
    } else if (knockoutAdvanceCount === 2) {
      const firstPlace = teamsByPosition[0];
      const secondPlace = teamsByPosition[1] ? [...teamsByPosition[1]].reverse() : [];

      for (let i = 0; i < Math.max(firstPlace.length, secondPlace.length); i++) {
        if (i < firstPlace.length) teamDescriptions.push(firstPlace[i]);
        if (i < secondPlace.length) teamDescriptions.push(secondPlace[i]);
      }
    } else {
      const higherRanked = teamsByPosition[0] || [];
      const lowerRankedPos = teamsByPosition[knockoutAdvanceCount - 1];
      const lowerRanked = lowerRankedPos ? [...lowerRankedPos].reverse() : [];

      for (let i = 0; i < Math.max(higherRanked.length, lowerRanked.length); i++) {
        if (i < higherRanked.length) teamDescriptions.push(higherRanked[i]);
        if (i < lowerRanked.length) teamDescriptions.push(lowerRanked[i]);
      }
    }

    const pairings: Array<{ team1: string; team2: string }> = [];
    for (let i = 0; i < teamDescriptions.length; i += 2) {
      if (i + 1 < teamDescriptions.length) {
        pairings.push({
          team1: teamDescriptions[i],
          team2: teamDescriptions[i + 1],
        });
      } else {
        pairings.push({ team1: teamDescriptions[i], team2: "BYE" });
      }
    }

    return pairings;
  };

  // Wildcard cross-group picks (e.g. "best runner-up across all groups"), when configured.
  // Feeds the same top/bottom-half cross-seed the backend's seedTourCupTeams() uses, so the
  // preview matches what generate-knockout will actually produce.
  const generateRuleBasedDoubleBracketSeeding = (): {
    mastersCup: Array<{ team1: string; team2: string }>;
    tourCup: Array<{ team1: string; team2: string }>;
  } => {
    const claimedGroupPositions = new Set<string>();
    const wildcardBudgetByPosition = new Map<number, number>();

    const mastersDescriptors = buildAdvancementDescriptorList(
      mastersCupAdvancementRules || [],
      numberOfGroups,
      teamsPerGroup,
      groupNames,
      claimedGroupPositions,
      wildcardBudgetByPosition,
    );
    const tourDescriptors = buildAdvancementDescriptorList(
      tourCupAdvancementRules?.length ? tourCupAdvancementRules : [{ type: "remaining" }],
      numberOfGroups,
      teamsPerGroup,
      groupNames,
      claimedGroupPositions,
      wildcardBudgetByPosition,
    );

    const crossSeed = (descriptors: string[]): Array<{ team1: string; team2: string }> => {
      const half = Math.floor(descriptors.length / 2);
      const top = descriptors.slice(0, half);
      const bottom = [...descriptors.slice(half)].reverse();
      const seeded: string[] = [];
      for (let i = 0; i < Math.max(top.length, bottom.length); i++) {
        if (i < top.length) seeded.push(top[i]);
        if (i < bottom.length) seeded.push(bottom[i]);
      }
      const pairings: Array<{ team1: string; team2: string }> = [];
      for (let i = 0; i < seeded.length; i += 2) {
        if (i + 1 < seeded.length) pairings.push({ team1: seeded[i], team2: seeded[i + 1] });
        else pairings.push({ team1: seeded[i], team2: "BYE" });
      }
      return pairings;
    };

    return { mastersCup: crossSeed(mastersDescriptors), tourCup: crossSeed(tourDescriptors) };
  };

  const ruleBasedSeeding =
    knockoutFormatType === "double-bracket" && mastersCupAdvancementRules?.length
      ? generateRuleBasedDoubleBracketSeeding()
      : null;

  const mastersCupPairingsDefault = knockoutFormatType === "double-bracket"
    ? (ruleBasedSeeding ? ruleBasedSeeding.mastersCup : generateMastersCupSeeding())
    : [];
  const tourCupPairingsDefault = knockoutFormatType === "double-bracket"
    ? (ruleBasedSeeding ? ruleBasedSeeding.tourCup : generateTourCupSeeding())
    : [];
  const singleBracketPairingsDefault = knockoutFormatType === "single-bracket" ? generateSingleBracketSeeding() : [];

  // Helper: convert flat seeding array to pairings
  const flatToPairings = (flat: string[]): Array<{ team1: string; team2: string }> => {
    const pairings: Array<{ team1: string; team2: string }> = [];
    for (let i = 0; i < flat.length; i += 2) {
      if (i + 1 < flat.length) {
        pairings.push({ team1: flat[i], team2: flat[i + 1] });
      }
    }
    return pairings;
  };

  // Helper: convert pairings to flat seeding array
  const pairingsToFlat = (pairings: Array<{ team1: string; team2: string }>): string[] => {
    const flat: string[] = [];
    pairings.forEach(p => { flat.push(p.team1, p.team2); });
    return flat;
  };

  // A real drag-and-drop customization only ever reorders the current default's labels, so its
  // flat array is always a permutation of the current default's flat label set. If the group
  // count, teams-per-group, advance counts, or advancement rules changed since the custom seeding
  // was saved, old descriptors like "5th B" or "Best 3rd Place #2" can reference positions/rules
  // that no longer exist — comparing as a multiset (order-independent) catches that staleness
  // without needing to persist a separate "what config was this computed for" signature.
  const isSameLabelMultiset = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((label, i) => label === sortedB[i]);
  };

  // Initialize pairings state when dialog opens or params change
  useEffect(() => {
    if (!open) return;

    const mastersDefaultFlat = pairingsToFlat(mastersCupPairingsDefault);
    const tourDefaultFlat = pairingsToFlat(tourCupPairingsDefault);
    const singleDefaultFlat = pairingsToFlat(singleBracketPairingsDefault);

    const validMastersCustom =
      initialCustomSeeding?.mastersCup?.length &&
      isSameLabelMultiset(initialCustomSeeding.mastersCup, mastersDefaultFlat);
    const validTourCustom =
      initialCustomSeeding?.tourCup?.length &&
      isSameLabelMultiset(initialCustomSeeding.tourCup, tourDefaultFlat);
    const validSingleCustom =
      initialCustomSeeding?.singleBracket?.length &&
      isSameLabelMultiset(initialCustomSeeding.singleBracket, singleDefaultFlat);

    setMastersCupPairingsState(
      validMastersCustom ? flatToPairings(initialCustomSeeding!.mastersCup!) : mastersCupPairingsDefault
    );
    setTourCupPairingsState(
      validTourCustom ? flatToPairings(initialCustomSeeding!.tourCup!) : tourCupPairingsDefault
    );
    setSingleBracketPairingsState(
      validSingleCustom ? flatToPairings(initialCustomSeeding!.singleBracket!) : singleBracketPairingsDefault
    );
    setIsCustomized(!!(validMastersCustom || validTourCustom || validSingleCustom));

    // Tell the parent about anything we just discarded so its own saved state (and the tournament
    // document on next save) drops the stale descriptors too — otherwise they'd keep coming back
    // every time this dialog reopens, even though they're invisible until then.
    const staleMasters = !!initialCustomSeeding?.mastersCup?.length && !validMastersCustom;
    const staleTour = !!initialCustomSeeding?.tourCup?.length && !validTourCustom;
    const staleSingle = !!initialCustomSeeding?.singleBracket?.length && !validSingleCustom;
    if ((staleMasters || staleTour || staleSingle) && onSeedingChange) {
      onSeedingChange({
        mastersCup: validMastersCustom ? initialCustomSeeding!.mastersCup : undefined,
        tourCup: validTourCustom ? initialCustomSeeding!.tourCup : undefined,
        singleBracket: validSingleCustom ? initialCustomSeeding!.singleBracket : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, numberOfGroups, teamsPerGroup, knockoutFormatType, mastersCupAdvanceCount, tourCupAdvanceCount, knockoutAdvanceCount, mastersCupAdvancementRules, tourCupAdvancementRules]);

  // Notify parent of seeding changes
  const notifySeeding = useCallback((
    mc: Array<{ team1: string; team2: string }>,
    tc: Array<{ team1: string; team2: string }>,
    sb: Array<{ team1: string; team2: string }>,
  ) => {
    if (!onSeedingChange) return;
    const data: CustomSeedingData = {};
    if (knockoutFormatType === "double-bracket") {
      data.mastersCup = pairingsToFlat(mc);
      data.tourCup = pairingsToFlat(tc);
    } else {
      data.singleBracket = pairingsToFlat(sb);
    }
    onSeedingChange(data);
  }, [onSeedingChange, knockoutFormatType]);

  // Parse a drag ID: "masters-0-team1" => { bracket: "masters", matchIndex: 0, slot: "team1" }
  const parseDragId = (id: string) => {
    const parts = id.split("-");
    const bracket = parts[0]; // masters, tour, single
    const matchIndex = parseInt(parts[1], 10);
    const slot = parts[2] as "team1" | "team2";
    return { bracket, matchIndex, slot };
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = parseDragId(active.id as string);
    const to = parseDragId(over.id as string);

    // Only allow swaps within the same bracket
    if (from.bracket !== to.bracket) return;

    const getPairings = (bracket: string) => {
      if (bracket === "masters") return [...mastersCupPairingsState];
      if (bracket === "tour") return [...tourCupPairingsState];
      return [...singleBracketPairingsState];
    };

    const pairings = getPairings(from.bracket);
    const fromPairing = pairings[from.matchIndex];
    const toPairing = pairings[to.matchIndex];
    if (!fromPairing || !toPairing) return;

    // Get the team labels from source and destination
    const fromLabel = fromPairing[from.slot];
    const toLabel = toPairing[to.slot];

    // Swap the two team labels
    pairings[from.matchIndex] = { ...fromPairing, [from.slot]: toLabel };
    pairings[to.matchIndex] = { ...toPairing, [to.slot]: fromLabel };

    if (from.bracket === "masters") {
      setMastersCupPairingsState(pairings);
      notifySeeding(pairings, tourCupPairingsState, singleBracketPairingsState);
    } else if (from.bracket === "tour") {
      setTourCupPairingsState(pairings);
      notifySeeding(mastersCupPairingsState, pairings, singleBracketPairingsState);
    } else {
      setSingleBracketPairingsState(pairings);
      notifySeeding(mastersCupPairingsState, tourCupPairingsState, pairings);
    }
    setIsCustomized(true);
  };

  const handleResetSeeding = () => {
    setMastersCupPairingsState(mastersCupPairingsDefault);
    setTourCupPairingsState(tourCupPairingsDefault);
    setSingleBracketPairingsState(singleBracketPairingsDefault);
    setIsCustomized(false);
    if (onSeedingChange) {
      onSeedingChange({});
    }
  };

  // Get label for the currently dragged item
  const getActiveDragLabel = (): string => {
    if (!activeDragId) return "";
    const { bracket, matchIndex, slot } = parseDragId(activeDragId);
    const pairings = bracket === "masters" ? mastersCupPairingsState
      : bracket === "tour" ? tourCupPairingsState
      : singleBracketPairingsState;
    return pairings[matchIndex]?.[slot] || "";
  };

  const getRoundName = (totalMatches: number): string => {
    if (totalMatches === 1) return "Final";
    if (totalMatches === 2) return "Semi-Finals";
    if (totalMatches === 4) return "Quarter-Finals";
    return `Round of ${totalMatches * 2}`;
  };

  // Render a bracket section with drag-and-drop pairings
  const renderBracketSection = (
    pairings: Array<{ team1: string; team2: string }>,
    bracketKey: string,
    color: "primary" | "secondary" | "info",
    title: string,
    subtitle: string,
    bgColor: string,
  ) => (
    <Paper sx={{ p: 2, mb: 3, bgcolor: bgColor }}>
      <Typography variant="h6" gutterBottom sx={{ color: `${color}.main` }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>

      <Grid container spacing={2}>
        {pairings.map((pairing, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "white",
                borderColor: `${color}.main`,
                borderWidth: 2,
              }}
            >
              <Typography variant="caption" color={color} fontWeight="bold">
                Match {index + 1}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <DraggableChip
                  id={`${bracketKey}-${index}-team1`}
                  label={pairing.team1}
                  color={color}
                  variant="filled"
                />
                <Box sx={{ textAlign: "center", my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    vs
                  </Typography>
                </Box>
                <DraggableChip
                  id={`${bracketKey}-${index}-team2`}
                  label={pairing.team2}
                  color={color}
                  variant="outlined"
                />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6">Knockout Stage Seeding Preview</Typography>
            {isCustomized && (
              <Typography variant="caption" color="warning.main" fontWeight="bold">
                Custom seeding applied
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {isCustomized && (
              <Tooltip title="Reset to default seeding">
                <IconButton onClick={handleResetSeeding} size="small" color="warning">
                  <ResetIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            <InfoIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Cross-Seeding Strategy
          </Typography>
          <Typography variant="body2">
            Teams are seeded to balance competition: higher ranked teams from early groups face
            lower ranked teams from later groups. This prevents all group winners from being on
            the same side of the bracket.
          </Typography>
        </Alert>

        {!!mastersCupAdvancementRules?.length && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Wildcard slots</strong> (e.g. "Best 2nd Place #1") represent a cross-group rule and can't
              be tied to a specific group until group results are final — the actual team is resolved automatically
              when the knockout bracket is generated.
            </Typography>
          </Alert>
        )}

        <Alert severity="success" icon={<SwapIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Drag & drop</strong> any team chip onto another within the same bracket to swap their positions.
            Changes will be applied when the knockout bracket is generated.
          </Typography>
        </Alert>

        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {knockoutFormatType === "double-bracket" && (
            <>
              {renderBracketSection(
                mastersCupPairingsState,
                "masters",
                "primary",
                `${bracketNames.primary} (Winners Bracket)`,
                `${getRoundName(mastersCupPairingsState.length)} • ${mastersCupPairingsState.length * 2} teams`,
                "primary.lighter",
              )}
              {tourCupPairingsState.length > 0 && renderBracketSection(
                tourCupPairingsState,
                "tour",
                "secondary",
                `${bracketNames.secondary} (Consolation Bracket)`,
                `${getRoundName(tourCupPairingsState.length)} • ${tourCupPairingsState.length * 2} teams`,
                "secondary.lighter",
              )}
            </>
          )}

          {knockoutFormatType === "single-bracket" && renderBracketSection(
            singleBracketPairingsState,
            "single",
            "info",
            "Single Elimination Bracket",
            `${getRoundName(singleBracketPairingsState.length)} • ${singleBracketPairingsState.length * 2} teams`,
            "info.lighter",
          )}

          <DragOverlay>
            {activeDragId ? (
              <Chip
                label={getActiveDragLabel()}
                size="small"
                sx={{ fontWeight: "bold", cursor: "grabbing" }}
                color="default"
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {isCustomized && (
          <Button onClick={handleResetSeeding} startIcon={<ResetIcon />} color="warning">
            Reset to Default
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KnockoutSeedingPreview;
